import { ParameterTypes, PostbackWindowStartingPoint, SupportTypes, TemplateTypes } from '../types';
import { PARTNER_CONFIGURATION_EVENT_PREFIX, trackMixpanelEvent } from '../../../utils/general';
import { FILTERS_ACTIONS, POSTBACK_FILTERS_TYPES } from './advancedPostbackSettings/PostbackFilters';

export const trackPartnerConfigMixpanelEvent = (eventName, partner, site = null, params = null) => {
    trackMixpanelEvent(PARTNER_CONFIGURATION_EVENT_PREFIX, eventName, {
        ...(partner ? { partnerName: partner.displayName } : {}),
        ...(site ? { platform: site.platform } : {}),
        ...(params || {}),
    });
};

const getSupportedParametersIds = (partner, platform, templateType) => {
    return new Set(
        partner.postbackTemplates
            .filter(template => template.platform === platform && template.type === templateType)
            .map(({ parameters }) => parameters)
            .flat()
    );
};

export const getUsedParameters = (partner, platform, templateType, parameterTypes) => {
    const supportedParametersIds = getSupportedParametersIds(partner, platform, templateType);

    return partner.parameters.filter(({ id, type }) => supportedParametersIds.has(id) && parameterTypes.includes(type));
};

export const getUsedPrivacyParameters = (partner, platform, templateType, parameterTypes) => {
    if (partner.privacyParameters) {
        const supportedParametersIds = getSupportedParametersIds(partner, platform, templateType);

        return partner.privacyParameters.filter(
            ({ id, type }) => supportedParametersIds.has(id) && parameterTypes.includes(type)
        );
    }
    return [];
};

export const parsePostbackFiltersFromServer = (filterString, availableFilters = []) => {
    if (!filterString || filterString.trim() === '') {
        return [];
    }

    // Split by " AND " to get individual filter conditions
    const conditions = filterString.split(' AND ');

    return conditions
        .map((condition, index) => {
            const trimmedCondition = condition.trim();
            const isExclude = trimmedCondition.startsWith('NOT ');

            // Remove "NOT " prefix if present
            const cleanCondition = isExclude ? trimmedCondition.substring(4).trim() : trimmedCondition;

            // Check if it has outer parentheses
            const hasParentheses = cleanCondition.startsWith('(') && cleanCondition.endsWith(')');

            // Remove outer parentheses if present
            const innerCondition = hasParentheses
                ? cleanCondition.substring(1, cleanCondition.length - 1)
                : cleanCondition;

            // Check if it contains OR (indicates multi-select with multiple values)
            const isMultiSelect = innerCondition.includes(' OR ');

            // Determine initial action based on whether it's multi-select
            const action = isMultiSelect ? (isExclude ? FILTERS_ACTIONS.EXCLUDE : FILTERS_ACTIONS.INCLUDE) : null;

            // Check if it contains OR (multiple values)
            if (isMultiSelect) {
                // Parse multiple values
                const orConditions = innerCondition.split(' OR ');
                const firstCondition = orConditions[0].trim();

                // Extract filter name - try with quotes first, then without
                let filterNameMatch = firstCondition.match(/"([^"]+)"=/);
                if (!filterNameMatch) {
                    filterNameMatch = firstCondition.match(/\{([^}]+)\}=/);
                }
                if (!filterNameMatch) {
                    filterNameMatch = firstCondition.match(/([^=]+)=/);
                }
                const filterName = filterNameMatch ? filterNameMatch[1].trim() : '';
                const fullFilterName = filterName.startsWith('{') ? filterName : `{${filterName}}`;

                const filterValue = orConditions
                    .map(orCond => {
                        const trimmed = orCond.trim();
                        // Try with quotes: "key"="value"
                        let valueMatch = trimmed.match(/="([^"]+)"/);
                        if (!valueMatch) {
                            // Try without quotes: key=value
                            valueMatch = trimmed.match(/=([^)]+)/);
                        }
                        return valueMatch ? valueMatch[1].trim() : '';
                    })
                    .filter(Boolean);

                return {
                    filter: fullFilterName,
                    action,
                    filterValue,
                };
            } else {
                // Parse single value with operator
                // Try to match: "key"[operator]"value" where operator can be =, >, <
                let filterNameMatch = innerCondition.match(/"([^"]+)"([=><])/);
                let operatorMatch = null;
                let valueMatch = null;

                if (filterNameMatch) {
                    operatorMatch = filterNameMatch[2];
                    // Extract value after operator
                    valueMatch = innerCondition.match(/[=><]"([^"]+)"/);
                } else {
                    // Try without quotes: {key}[operator]value or key[operator]value
                    filterNameMatch = innerCondition.match(/\{([^}]+)\}([=><])/);
                    if (!filterNameMatch) {
                        filterNameMatch = innerCondition.match(/([^=><]+)([=><])/);
                    }
                    if (filterNameMatch) {
                        operatorMatch = filterNameMatch[2];
                        valueMatch = innerCondition.match(/[=><]([^)]+)/);
                    }
                }

                if (filterNameMatch && valueMatch) {
                    const filterName = filterNameMatch[1].trim();
                    const fullFilterName = filterName.startsWith('{') ? filterName : `{${filterName}}`;
                    const filterValue = valueMatch[1].trim();

                    // Look up the filter type from availableFilters
                    const filterDef = availableFilters.find(f => f.name === fullFilterName);
                    const filterType = filterDef?.type;

                    // Check if it's a custom filter (not in availableFilters)
                    const isCustomFilter = !filterDef;

                    // Determine action and value type based on context:
                    // - If filterType is 'multi-select' OR custom filter, use include/exclude actions with array values
                    // - Otherwise (text/number), use equals/greater_than/smaller_than with string values

                    let parsedAction;
                    let parsedValue;

                    if (filterType === POSTBACK_FILTERS_TYPES.MULTI_SELECT || isCustomFilter) {
                        // Multi-select filter or custom filter with single value
                        parsedAction = isExclude ? FILTERS_ACTIONS.EXCLUDE : FILTERS_ACTIONS.INCLUDE;
                        parsedValue = [filterValue];
                    } else {
                        // Text/number filter - determine action based on operator
                        if (operatorMatch === '>') {
                            parsedAction = FILTERS_ACTIONS.GREATER_THAN;
                        } else if (operatorMatch === '<') {
                            parsedAction = FILTERS_ACTIONS.SMALLER_THAN;
                        } else if (operatorMatch === '=') {
                            if (isExclude) {
                                parsedAction = FILTERS_ACTIONS.EXCLUDE;
                            } else {
                                parsedAction = FILTERS_ACTIONS.EQUALS;
                            }
                        } else {
                            parsedAction = FILTERS_ACTIONS.EQUALS; // default
                        }
                        parsedValue = filterValue;
                    }

                    return {
                        filter: fullFilterName,
                        action: parsedAction,
                        filterValue: parsedValue,
                    };
                }
            }

            return null;
        })
        .filter(Boolean);
};

export const parsePostbackFiltersToServer = filters => {
    if (!filters || filters.length === 0) {
        return null;
    }

    const filterConditions = filters
        .filter(filter => {
            if (!filter.filter) return false;

            // For arrays (multi-select), check if non-empty
            if (Array.isArray(filter.filterValue)) {
                return filter.filterValue.length > 0;
            }

            // For single values, accept 0 but reject null, undefined, and empty string
            return filter.filterValue !== null && filter.filterValue !== undefined && filter.filterValue !== '';
        })
        .map(filter => {
            const { filter: filterName, action, filterValue } = filter;
            const isExclude = action === FILTERS_ACTIONS.EXCLUDE;

            // Handle array of values (multi-select with include/exclude)
            if (Array.isArray(filterValue) && filterValue.length > 0) {
                const valueConditions = filterValue
                    .map(value => {
                        // Check if value is a number (float or integer)
                        const numValue = Number(value);
                        const isNumber = !Number.isNaN(numValue) && value !== '' && value !== null;
                        // If number, don't use quotes; otherwise use quotes
                        return isNumber ? `${filterName}=${value}` : `"${filterName}"="${value}"`;
                    })
                    .join(' OR ');
                const condition = `(${valueConditions})`;
                return isExclude ? `NOT ${condition}` : condition;
            }

            // Handle single value (number with operators)
            if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
                let operator = '=';

                switch (action) {
                    case FILTERS_ACTIONS.EQUALS:
                        operator = '=';
                        break;
                    case FILTERS_ACTIONS.GREATER_THAN:
                        operator = '>';
                        break;
                    case FILTERS_ACTIONS.SMALLER_THAN:
                        operator = '<';
                        break;
                    default:
                        operator = '=';
                }

                const condition = `(${filterName}${operator}${filterValue})`;
                return isExclude ? `NOT ${condition}` : condition;
            }

            return null;
        })
        .filter(Boolean);

    return filterConditions.length > 0 ? filterConditions.join(' AND ') : null;
};

const parseAttributionPostbackToServer = postback => {
    if (!postback?.selected) return null;

    const { sendAll, sendViewThrough, campaignRegex, postbackUri, customByUser, filters } = postback;
    return {
        sendAll,
        sendViewThrough,
        campaignRegex: sendAll ? null : campaignRegex,
        postbackUri: customByUser ? postbackUri : null,
        customByUser,
        filters: sendAll ? null : parsePostbackFiltersToServer(filters),
    };
};

const parseEventPostbackToServer = postback => {
    const {
        eventId,
        eventName: _,
        postbackWindow,
        postbackWindowStartingPoint,
        sendAll,
        sendRevenue,
        campaignRegex,
        postbackUri,
        customByUser,
        filters,
        disabled: __,
        ...params
    } = postback;

    return {
        eventId,
        sendAll,
        installWindow: postbackWindowStartingPoint === PostbackWindowStartingPoint.INSTALL ? postbackWindow : null,
        clickWindow: postbackWindowStartingPoint === PostbackWindowStartingPoint.TOUCHPOINT ? postbackWindow : null,
        parameters: params,
        sendRevenue,
        campaignRegex: sendAll ? null : campaignRegex,
        postbackUri: customByUser ? postbackUri : null,
        customByUser,
        filters: sendAll ? null : parsePostbackFiltersToServer(filters),
    };
};

const parseWindows = (windows, partner, platform) => {
    const installTemplate = getTemplate(partner, platform, TemplateTypes.INSTALL);
    const reengSupported = getPostbackSupported(installTemplate, partner.reengagementSupport) !== SupportTypes.NEVER;
    const viewSupported = partner.viewthroughSupport !== SupportTypes.NEVER;
    const ctvSupported = partner.ipProbabilisticSupport !== SupportTypes.NEVER;

    return {
        ...windows,
        installViewWindowDeterministic: viewSupported ? windows.installViewWindowDeterministic : 0,
        installViewWindowProbabilistic: viewSupported ? windows.installViewWindowProbabilistic : 0,
        reengClickWindowDeterministic: reengSupported ? windows.reengClickWindowDeterministic : 0,
        reengClickWindowProbabilistic: reengSupported ? windows.reengClickWindowProbabilistic : 0,
        reengViewWindowDeterministic: reengSupported && viewSupported ? windows.reengViewWindowDeterministic : 0,
        reengViewWindowProbabilistic: reengSupported && viewSupported ? windows.reengViewWindowProbabilistic : 0,
        ctvViewWindow: ctvSupported ? windows.ctvViewWindow : 0,
        ctvPrioritizationWindow: ctvSupported ? windows.ctvPrioritizationWindow : 0,
    };
};

export const parsePartnerConfigurationToServer = (partner, site, partnerConfiguration, agencyId) => {
    const {
        site: _,
        install,
        reengagement,
        fraudEnabled,
        customEventPostbacks,
        revenueEventPostbacks,
        geoRestrictions,
        attributionReturnRate,
        restrictLds,
        ldsDefault,
        accounts,
        anonymizePostbacks,
        windows,
        ...parameters
    } = partnerConfiguration;

    return {
        partner: partner.id,
        longname: site.longname,
        platform: site.platform,
        ldsOptions: {
            restrictLds,
            ldsDefault,
            anonymizePostbacks,
        },
        parameters,
        accounts,
        postbacks: {
            fraudEnabled,
            install: parseAttributionPostbackToServer(install),
            reengagement: parseAttributionPostbackToServer(reengagement),
            customEvents: customEventPostbacks?.map(parseEventPostbackToServer) || [],
            revenueEvents: revenueEventPostbacks?.map(parseEventPostbackToServer) || [],
        },
        geoRestrictions,
        attributionReturnRate: attributionReturnRate || null,
        windows,
        agencyId,
    };
};

const parseAttributionPostbackFromServer = (postback, availableFilters = []) => {
    if (!postback) return { selected: false };

    const { sendAll, sendViewThrough, campaignRegex, postbackUri, customByUser, filters } = postback;

    return {
        selected: true,
        sendAll,
        sendViewThrough,
        campaignRegex,
        postbackUri,
        customByUser,
        filters: parsePostbackFiltersFromServer(filters, availableFilters),
    };
};

const parseEventPostbackFromServer = (postback, eventTemplateParams, availableFilters = []) => {
    if (!postback) return null;

    const {
        eventId,
        eventName,
        installWindow,
        clickWindow,
        sendAll,
        parameters,
        sendRevenue,
        campaignRegex,
        postbackUri,
        customByUser,
        filters,
    } = postback;

    let postbackWindow = null;
    let postbackWindowStartingPoint = PostbackWindowStartingPoint.UNLIMITED;

    if (installWindow) {
        postbackWindow = installWindow;
        postbackWindowStartingPoint = sendAll ? postbackWindowStartingPoint : PostbackWindowStartingPoint.INSTALL;
    } else if (clickWindow) {
        postbackWindow = clickWindow;
        postbackWindowStartingPoint = sendAll ? postbackWindowStartingPoint : PostbackWindowStartingPoint.TOUCHPOINT;
    }

    // if we have defined pb param without value, add it
    const templateParameters = eventTemplateParams?.reduce((previousParam, nextParam) => {
        return { ...previousParam, ...(!parameters.hasOwnProperty(nextParam.name) && { [nextParam.name]: null }) };
    }, {});

    return {
        eventId,
        eventName,
        sendAll,
        postbackWindow,
        postbackWindowStartingPoint,
        sendRevenue,
        campaignRegex,
        postbackUri,
        customByUser,
        filters: parsePostbackFiltersFromServer(filters, availableFilters),
        ...parameters,
        ...templateParameters,
    };
};

export const parsePartnerConfigurationFromServer = (partnerConfiguration, partner, platform, availableFilters = []) => {
    const {
        siteId,
        parameters,
        postbacks,
        geoRestrictions,
        attributionReturnRate,
        ldsOptions,
        windows,
        accounts,
    } = partnerConfiguration;
    const { install, reengagement, revenueEvents, customEvents, fraudEnabled } = postbacks;

    const revenueTemplateParams = partner
        ? getUsedParameters(partner, platform, TemplateTypes.REVENUE, [ParameterTypes.POSTBACK])
        : [];
    const customTemplateParams = partner
        ? getUsedParameters(partner, platform, TemplateTypes.CUSTOM, [ParameterTypes.POSTBACK])
        : [];

    return {
        site: siteId,
        accounts,
        ...(parameters || {}),
        install: parseAttributionPostbackFromServer(install, availableFilters),
        reengagement: parseAttributionPostbackFromServer(reengagement, availableFilters),
        customEventPostbacks: customEvents?.length
            ? customEvents.map(event => parseEventPostbackFromServer(event, customTemplateParams, availableFilters))
            : null,
        revenueEventPostbacks: revenueEvents?.length
            ? revenueEvents.map(event => parseEventPostbackFromServer(event, revenueTemplateParams, availableFilters))
            : null,
        fraudEnabled,
        geoRestrictions,
        attributionReturnRate: attributionReturnRate || null,
        restrictLds: ldsOptions.restrictLds,
        ldsDefault: ldsOptions.ldsDefault,
        anonymizePostbacks: ldsOptions.anonymizePostbacks,
        windows: parseWindows(windows, partner, platform),
    };
};

export const getSendAllEventsTooltip = (sendAllSupport, isReadonly) => {
    if (sendAllSupport === SupportTypes.MANDATORY && !isReadonly)
        return 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.SEND_ALL_EVENTS_MANDATORY';
    if (sendAllSupport === SupportTypes.NEVER && !isReadonly)
        return 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.SEND_ALL_EVENTS_NOT_SUPPORTED';

    return null;
};

export const isDisabled = (value, readOnly) =>
    value === SupportTypes.NEVER || value === SupportTypes.MANDATORY || readOnly;

export const orgSupportAdvancedPostbacksFeatures = orgOptions => {
    const {
        trackerCampaignNameFilterSupported = false,
        advancedPostbackEditingSupported = false,
        flexiblePostbackSupported = false,
        postbackFiltersSupported = false,
    } = orgOptions;
    return (
        trackerCampaignNameFilterSupported ||
        advancedPostbackEditingSupported ||
        flexiblePostbackSupported ||
        postbackFiltersSupported
    );
};

export const getTemplate = (partner, sitePlatform, templateType) =>
    partner.postbackTemplates.find(({ type, platform }) => type === templateType && platform === sitePlatform);

export const getPostbackSupported = (installTemplate, postbackSupportType) => {
    return installTemplate ? postbackSupportType : SupportTypes.NEVER;
};

export const isSendAllWithFiltersPostback = postback => {
    return postback?.sendAll && (postback?.filters?.length || postback?.campaignRegex);
};
